import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import axios from 'axios';
import { Mail, MapPin, Phone, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  phone: z.string().optional(),
  business_name: z.string().optional(),
  website: z.string().optional(),
  project_type: z.string().min(1, 'Please select a project type'),
  budget_range: z.string().min(1, 'Please select a budget range'),
  timeline: z.string().min(1, 'Please select a timeline'),
  message: z.string().min(10, 'Message must be at least 10 characters'),
  free_audit: z.boolean().default(false),
});

export const ContactPage = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      free_audit: false,
    },
  });

  const freeAudit = watch('free_audit');

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/leads`, data);
      setIsSuccess(true);
      toast.success('Thank you! We\'ll be in touch soon.');
      reset();
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="contact-success">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="bg-white border border-slate-200 rounded-2xl p-12 shadow-lg">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-[#10B981]/10 rounded-full mb-6">
              <CheckCircle size={48} className="text-[#10B981]" />
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-testid="success-heading">
              Thank You!
            </h2>
            <p className="text-lg text-slate-600 mb-8" data-testid="success-message">
              We've received your inquiry and will get back to you within 24 hours. Check your email for confirmation.
            </p>
            <Button
              onClick={() => setIsSuccess(false)}
              className="bg-[#10B981] hover:bg-[#059669] text-white rounded-full px-8 py-6 text-lg"
              data-testid="success-new-request-button"
            >
              Submit Another Request
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-gradient py-20 md:py-32" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6" data-testid="contact-hero-heading">
            Let's Build Something Amazing
          </h1>
          <p className="text-lg md:text-xl text-slate-600 mb-8 max-w-3xl mx-auto" data-testid="contact-hero-description">
            Get in touch today for a free consultation. We'll discuss your goals and create a custom plan for your project.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div className="md:col-span-2">
              <h2 className="font-heading text-3xl font-bold text-slate-900 mb-6" data-testid="contact-info-heading">
                Get in Touch
              </h2>
              <p className="text-slate-600 mb-8" data-testid="contact-info-description">
                Ready to start your project? Fill out the form and we'll get back to you within 24 hours.
              </p>

              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4" data-testid="contact-info-email">
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <Mail size={24} className="text-[#10B981]" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-slate-900 mb-1">Email</h3>
                    <a href="mailto:contact@snappadeal.com" className="text-slate-600 hover:text-[#10B981]">
                      contact@snappadeal.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-info-location">
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <MapPin size={24} className="text-[#10B981]" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-slate-900 mb-1">Location</h3>
                    <p className="text-slate-600">Canada (Remote)</p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-testid="contact-info-book-call">
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <Phone size={24} className="text-[#10B981]" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-slate-900 mb-1">Book a Call</h3>
                    <a
                      href="mailto:contact@snappadeal.com?subject=Call booking request"
                      className="text-slate-600 hover:text-[#10B981]"
                    >
                      Schedule via email
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
                <p className="text-sm text-slate-600 mb-2">
                  <strong>Response Time:</strong> Within 24 hours
                </p>
                <p className="text-sm text-slate-600">
                  <strong>Available:</strong> Monday - Friday, 9 AM - 6 PM EST
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="md:col-span-3">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="mb-2 block">Name *</Label>
                    <Input
                      id="name"
                      {...register('name')}
                      placeholder="John Doe"
                      className="h-12"
                      data-testid="contact-input-name"
                    />
                    {errors.name && (
                      <p className="text-red-500 text-sm mt-1" data-testid="contact-error-name">
                        {errors.name.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="email" className="mb-2 block">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      {...register('email')}
                      placeholder="john@example.com"
                      className="h-12"
                      data-testid="contact-input-email"
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1" data-testid="contact-error-email">
                        {errors.email.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="phone" className="mb-2 block">Phone (Optional)</Label>
                    <Input
                      id="phone"
                      {...register('phone')}
                      placeholder="+1 (555) 000-0000"
                      className="h-12"
                      data-testid="contact-input-phone"
                    />
                  </div>

                  <div>
                    <Label htmlFor="business_name" className="mb-2 block">Business Name (Optional)</Label>
                    <Input
                      id="business_name"
                      {...register('business_name')}
                      placeholder="Your Company"
                      className="h-12"
                      data-testid="contact-input-business"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="website" className="mb-2 block">Current Website (Optional)</Label>
                  <Input
                    id="website"
                    {...register('website')}
                    placeholder="https://yourwebsite.com"
                    className="h-12"
                    data-testid="contact-input-website"
                  />
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <Label htmlFor="project_type" className="mb-2 block">Project Type *</Label>
                    <Select onValueChange={(value) => setValue('project_type', value)}>
                      <SelectTrigger className="h-12" data-testid="contact-select-project-type">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="landing-page">Landing Page</SelectItem>
                        <SelectItem value="business-website">Business Website</SelectItem>
                        <SelectItem value="ecommerce">E-commerce</SelectItem>
                        <SelectItem value="redesign">Website Redesign</SelectItem>
                        <SelectItem value="maintenance">Maintenance</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.project_type && (
                      <p className="text-red-500 text-sm mt-1" data-testid="contact-error-project-type">
                        {errors.project_type.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="budget_range" className="mb-2 block">Budget Range *</Label>
                    <Select onValueChange={(value) => setValue('budget_range', value)}>
                      <SelectTrigger className="h-12" data-testid="contact-select-budget">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="under-2k">Under $2,000</SelectItem>
                        <SelectItem value="2k-5k">$2,000 - $5,000</SelectItem>
                        <SelectItem value="5k-10k">$5,000 - $10,000</SelectItem>
                        <SelectItem value="10k-plus">$10,000+</SelectItem>
                        <SelectItem value="not-sure">Not Sure</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.budget_range && (
                      <p className="text-red-500 text-sm mt-1" data-testid="contact-error-budget">
                        {errors.budget_range.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="timeline" className="mb-2 block">Timeline *</Label>
                    <Select onValueChange={(value) => setValue('timeline', value)}>
                      <SelectTrigger className="h-12" data-testid="contact-select-timeline">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="asap">ASAP</SelectItem>
                        <SelectItem value="1-2-weeks">1-2 Weeks</SelectItem>
                        <SelectItem value="2-4-weeks">2-4 Weeks</SelectItem>
                        <SelectItem value="1-3-months">1-3 Months</SelectItem>
                        <SelectItem value="flexible">Flexible</SelectItem>
                      </SelectContent>
                    </Select>
                    {errors.timeline && (
                      <p className="text-red-500 text-sm mt-1" data-testid="contact-error-timeline">
                        {errors.timeline.message}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <Label htmlFor="message" className="mb-2 block">Project Details *</Label>
                  <Textarea
                    id="message"
                    {...register('message')}
                    placeholder="Tell us about your project, goals, and any specific requirements..."
                    rows={6}
                    data-testid="contact-input-message"
                  />
                  {errors.message && (
                    <p className="text-red-500 text-sm mt-1" data-testid="contact-error-message">
                      {errors.message.message}
                    </p>
                  )}
                </div>

                <div className="flex items-center space-x-2" data-testid="contact-checkbox-audit">
                  <Checkbox
                    id="free_audit"
                    checked={freeAudit}
                    onCheckedChange={(checked) => setValue('free_audit', checked)}
                  />
                  <Label htmlFor="free_audit" className="cursor-pointer">
                    Request Free Website Audit
                  </Label>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <p className="text-sm text-slate-600">
                    <strong>DM for price:</strong> Final pricing depends on project scope. We'll provide a detailed quote after our initial discussion.
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#10B981] hover:bg-[#059669] text-white rounded-full h-14 text-lg font-bold shadow-lg shadow-green-500/20 hover:shadow-green-500/40 transition-all duration-300"
                  data-testid="contact-submit-button"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send Inquiry'
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
